import styled, { css } from 'styled-components';

import { ButtonLink } from '@telialt/common-button-link/src';
import { purpurFocus } from '@telialt/common-theme/src/mixins';
import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurSpacing,
    getPurpurTypography,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';

type WrapperProps = {
    $isHidden?: boolean;
};

export const ComparedProductsWrapper = styled.section<WrapperProps>`
    ${({ $isHidden }) =>
        $isHidden
            ? css`
                  position: absolute;
                  left: -10000px;
                  width: 1px;
                  height: 1px;
                  overflow: hidden;
              `
            : css`
                  position: sticky;
                  left: auto;
                  width: auto;
                  height: auto;
              `};
    bottom: -1px;
    background: ${getPurpurColor('functionalWhite')};
    box-shadow: 0px 0px 16px 0px rgba(34, 34, 34, 0.16);
    padding: ${getPurpurSpacing('200')} 0;
    z-index: 10;
`;

export const ComparedProductsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${getPurpurSpacing('200')};

    ${purpurMedia('lg')`
        gap: ${getPurpurSpacing('300')};
        height: 8.125rem;
        grid-template-columns: repeat(4, minmax(0, 1fr));
    `};
`;

export const ComparedProductCardContainer = styled.div`
    border-radius: ${getPurpurBorder('radiusMd')};
    border: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray300')};
    padding: ${getPurpurSpacing('200')};
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: stretch;

    &:nth-child(3) {
        display: none;
    }

    ${purpurMedia('lg')`
        &:nth-child(3) {
            display: flex;
        }
    `};
`;

export const ComparedProductCardProductInfo = styled.div`
    display: flex;
    align-items: center;
    gap: ${getPurpurSpacing('100')};
`;

export const ComparedProductCardImageContainer = styled.div`
    position: relative;
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;

    img {
        object-fit: contain;
    }

    ${purpurMedia('lg')`
        width: 3.125rem;
        height: 3.125rem;
    `};
`;

export const ComparedProductCardProductName = styled.a`
    font-weight: ${getPurpurTypography('weightMedium')};
    font-size: ${getPurpurTypography('scale75')};
    color: ${getPurpurColor('textInteractivePrimary')};
    word-break: break-word;
    text-decoration: none;

    ${purpurMedia('lg')`
        font-weight: ${getPurpurTypography('weightBold')};
        font-size: ${getPurpurTypography('scale100')};
    `};

    ${purpurFocus()};
`;

export const ComparedProductCardAction = styled.div`
    display: flex;
    justify-content: center;
    margin-top: ${getPurpurSpacing('100')};
`;

export const ComparedProductPlaceholder = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: ${getPurpurBorder('radiusMd')};
    border: ${getPurpurBorder('widthSm')} dashed ${getPurpurColor('gray200')};

    &:nth-child(3) {
        display: none;
    }

    ${purpurMedia('lg')`
        &:nth-child(3) {
            display: flex;
        }
    `};
`;

export const CompareActionsContainer = styled.div`
    grid-column: span 2;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: ${getPurpurSpacing('200')};

    ${purpurMedia('lg')`
        grid-column: auto;
        flex-direction: column;
        justify-content: space-between;
        padding-top: ${getPurpurSpacing('100')};
        padding-bottom: ${getPurpurSpacing('100')};
    `};
`;

export const CompareButton = styled(ButtonLink)`
    flex: 1;

    ${purpurMedia('md')`
        flex: initial;
    `};
`;
